/* eslint-disable react/no-array-index-key */
import React, { memo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { getRadishRecordList, UserRadishRecordItem, ProjectType } from '$/api/server';
import { useOfficialStore } from '$/store/useOfficialStore';
import { message } from '$/components/Message';
import Input from '$/components/base/Input';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';

import styles from './index.module.scss';

export interface TabItem {
  label: string;
  key: string;
}

export interface CalcTabsProps {
  items: TabItem[];

}

const PAGE_COUNT = 6;

/**
 * 萝卜明细 Tab（获取 / 消费）与分页列表。
 */
function CalcTabs(props: CalcTabsProps) {
  const { items = [] } = props;
  const navigateMap = useOfficialStore((state) => state.navigateMap);
  const [radishDetailList, setRadishDetailList] = useState<Awaited<ReturnType< typeof getRadishRecordList>>['record_list']>([]);
  const [maxPage, setMaxPage] = useState(1);
  const [page, setPage] = useState('1');
  const [currentTab, setCurrentTab] = useState(2);
  const languageStore = useLanguageStore((state) => state.languageStore);

  useEffect(() => {
    if (!page) {
      return undefined;
    }
    let cancelled = false;
    const pageNum = Number(page);
    const type = currentTab as 1 | 2;

    getRadishRecordList(type, pageNum, PAGE_COUNT).then((res) => {
      if (cancelled || !res?.record_list) {
        return;
      }
      setMaxPage(Math.ceil(res.total / PAGE_COUNT));
      setRadishDetailList(res.record_list || []);
    });

    return () => {
      cancelled = true;
    };
  }, [page, currentTab]);

  // 获取明细
  // useEffect(() => { fetchRadishDetailList(1); }, []);
  const onChangeTab = (current: number) => {
    setCurrentTab(current + 1);
    setPage('1');
  };

  const clickDetailName = (detailItem: UserRadishRecordItem) => {
    if (detailItem?.del_status === 1) {
      message.error(languageStore.smartcatTranslationText((v) => v.Room_deleted));
      return;
    }
    const { room_id, room_type } = detailItem;
    if (!room_id) {
      return;
    }
    // Record 键需覆盖完整 ProjectType，枚举新增成员时编译会要求补全
    const navigateByRoomType: Record<ProjectType, () => void> = {
      [ProjectType.Canvas]: () => navigateMap.historyStudio({ room_id }),
      [ProjectType.Workflow]: () => navigateMap.historyFlow({ room_id }),
      [ProjectType.AgentTeam]: () => navigateMap.historyTeamFlow({ room_id }),
    };
    navigateByRoomType[room_type]();
  };

  return (
    <div className={styles['calc-tabs-container']}>
      <div className={styles['calc-tabs-header']}>
        {items.map((item, index) => (
          <div key={index} className={classNames(styles['calc-tab-item'], { [styles['calc-tab-item-active']]: currentTab === index + 1 })} onClick={() => { onChangeTab(index); }}>
            <div className={styles['calc-tab-item-label']}>
              {item.label}

            </div>
          </div>
        ))}
      </div>
      {radishDetailList?.length
        ? (
          <div className={styles['calc-tab-list']}>
            {radishDetailList.map((detailItem, index) => (
              <div className={styles['calc-detail-item']} key={index}>
                <div className={styles['calc-detail-item-left']}>
                  <div
                    className={classNames(styles['calc-detail-item-name'], { [styles['calc-detail-item-name-link']]: detailItem?.room_id })}
                    onClick={() => clickDetailName(detailItem)}
                  >
                    {detailItem.room_title}
                  </div>
                  <div className={styles['calc-detail-item-room-title']}>

                    {detailItem.scene_name}
                    {detailItem?.reduce_media_num ? ` ${detailItem.reduce_media_num}` : null}
                    {detailItem?.unit ? `${detailItem.unit}` : null}
                  </div>
                  <div className={styles['calc-detail-item-date']}>
                    {detailItem.consume_time}
                  </div>
                </div>
                <div className={styles['calc-detail-item-right']}>
                  {currentTab === 1 ? '+' : '-'}
                  {detailItem.amount}
                </div>
              </div>
            ))}
          </div>
        ) : <div className={styles['empty-data']}>{languageStore.smartcatTranslationText((v) => v.Roboneo_web_no_data)}</div>}
      {radishDetailList?.length ? (
        <div className={styles['footer-input-paganation']}>
          <IconFont
            className={classNames(styles['desc-left-arrow'], { [styles['page-disabled']]: Number(page) === 1 || Number(page) === 0 })}
            name="chevronRightBold"
            fontSize={16}
            color="var(--content-buttonSecondary)"
            onClick={() => {
              if (Number(page) === 1) return;
              setPage(`${Number(page) - 1}`);
            }}
          />

          <Input
            style={{ margin: '10px' }}
            inputClassName={styles['pagation-input']}
            value={page}
            onChange={(e) => {
              const { value } = e.target;

              if (!/^\d+$/.test(value) || value === '0') {
                // 如果输入不是正整数，清空输入框
                setPage('');
              } else {
                if (Number(value) > maxPage) return;
                setPage(value);
              }
            }}
          />
          <div className={styles['max-page']}>
            <span style={{ marginRight: '2px' }}>/</span>
            {maxPage}
          </div>
          <IconFont
            className={classNames({ [styles['page-disabled']]: Number(page) === maxPage })}
            name="chevronRightBold"
            fontSize={16}
            color="var(--content-buttonSecondary)"
            onClick={() => {
              if (Number(page) >= maxPage) return;
              setPage(`${Number(page) + 1}`);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
export default memo(CalcTabs);
